import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import Footer from '../../components/Footer/Footer';
import Menu from '../../components/Menu/Menu';
import Toast from '../../components/Toast/Toast';
import CopyButton from '../../components/CopyButton/CopyButton';
import KeyTestResult from './KeyTestResult';

import {
    getSettings,
    testApiKey,
    testSmtp,
    updateSettings,
} from '../../services/SettingsService';

const GMAIL_SMTP = { smtpHost: 'smtp.gmail.com', smtpPort: 587 };

// Binance's own error body ({code, message, friendly?}) when the key test
// fails, or a plain string for any other error (e.g. missing key). The
// backend adds `friendly` for known key rejections (invalid key, IP,
// permissions); the raw Binance message is always kept for support.
function describeKeyTestError(err) {
    const data = err.response ? err.response.data : err.message;
    if (data && typeof data === 'object' && data.message)
        return { message: data.message, friendly: data.friendly || null };
    return { message: data, friendly: null };
}

function Settings() {
    const confirmPassword = useRef('');
    const [settings, setSettings] = useState({});
    const [notification, setNotification] = useState({});
    const [testing, setTesting] = useState(false);
    const [testingSpotKey, setTestingSpotKey] = useState(false);
    const [spotKeyResult, setSpotKeyResult] = useState(null);
    const [testingFuturesKey, setTestingFuturesKey] = useState(false);
    const [futuresKeyResult, setFuturesKeyResult] = useState(null);

    useEffect(() => {
        getSettings()
            .then(result => {
                setSettings(result);
            })
            .catch(err => {
                console.error(err.response ? err.response.data : err.message);
                setNotification({
                    type: 'error',
                    text: err.response ? err.response.data : err.message,
                });
            });
    }, []);

    function onInputChange(event) {
        setSettings(prevState => ({
            ...prevState,
            [event.target.id]: event.target.value,
        }));
    }

    function onFormSubmit(event) {
        if (
            (settings.password || confirmPassword.current.value) &&
            settings.password !== confirmPassword.current.value
        )
            return setNotification({
                type: 'error',
                text: `The fields New Password and Confirm Password must be equal.`,
            });

        updateSettings(settings)
            .then(result => {
                if (result) {
                    setSettings(prevState => ({
                        ...prevState,
                        smtpPass: '',
                        hasSmtpPass: result.hasSmtpPass,
                        channels: result.channels,
                    }));
                    setNotification({
                        type: 'success',
                        text: `Settings saved successfully!`,
                    });
                } else setNotification({ type: 'error', text: result });
            })
            .catch(err => {
                console.error(err.response ? err.response.data : err.message);
                setNotification({
                    type: 'error',
                    text: err.response ? err.response.data : err.message,
                });
            });
    }

    function onUseGmailClick() {
        setSettings(prevState => ({ ...prevState, ...GMAIL_SMTP }));
    }

    function onTestKeyClick(isFuture) {
        const setTestingKey = isFuture
            ? setTestingFuturesKey
            : setTestingSpotKey;
        const setKeyResult = isFuture ? setFuturesKeyResult : setSpotKeyResult;

        setTestingKey(true);
        setKeyResult(null);
        testApiKey(settings, isFuture)
            .then(result => setKeyResult({ ok: true, data: result }))
            .catch(err =>
                setKeyResult({ ok: false, ...describeKeyTestError(err) }),
            )
            .finally(() => setTestingKey(false));
    }

    function onTestSmtpClick() {
        setTesting(true);
        testSmtp(settings)
            .then(() =>
                setNotification({
                    type: 'success',
                    text: `Test e-mail sent to ${settings.email}. Check your inbox.`,
                }),
            )
            .catch(err => {
                console.error(err.response ? err.response.data : err.message);
                setNotification({
                    type: 'error',
                    text: err.response ? err.response.data : err.message,
                });
            })
            .finally(() => setTesting(false));
    }

    return (
        <>
            <Menu />
            <main className="content">
                <div className="d-flex justify-content-between flex-wrap flex-md-nowrap align-items-center py-4">
                    <div className="d-block mb-4 mb-md-0">
                        <h1 className="h4">Settings</h1>
                    </div>
                </div>
                <div className="row">
                    <div className="col-12">
                        <div className="card card-body border-0 shadow mb-4">
                            <h2 className="h5 mb-4">Personal Settings</h2>

                            <div className="row">
                                <div className="col-md-6 mb-3">
                                    <div className="form-group">
                                        <label htmlFor="email">Email</label>
                                        <input
                                            className="form-control"
                                            id="email"
                                            type="email"
                                            placeholder="name@company.com"
                                            value={settings.email || ''}
                                            onChange={onInputChange}
                                        />
                                    </div>
                                </div>
                                <div className="col-md-6 mb-3">
                                    <div className="form-group">
                                        <label htmlFor="phone">Cellphone</label>
                                        <input
                                            className="form-control"
                                            id="phone"
                                            type="text"
                                            placeholder="+1 51 123456789"
                                            value={settings.phone || ''}
                                            onChange={onInputChange}
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className="row">
                                <div className="col-md-6 mb-3">
                                    <div>
                                        <label htmlFor="password">
                                            New Password
                                        </label>
                                        <input
                                            className="form-control"
                                            id="password"
                                            type="password"
                                            placeholder="Enter your new password"
                                            onChange={onInputChange}
                                        />
                                    </div>
                                </div>
                                <div className="col-md-6 mb-3">
                                    <div>
                                        <label htmlFor="confirmPassword">
                                            Confirm Password
                                        </label>
                                        <input
                                            ref={confirmPassword}
                                            className="form-control"
                                            id="confirmPassword"
                                            type="password"
                                            placeholder="Your new password again"
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className="row">
                                <div className="col-md-6 mb-3">
                                    <div className="form-group">
                                        <label htmlFor="telegramChat">
                                            Telegram Chat ID
                                        </label>
                                        {settings.telegramBot && (
                                            <a
                                                href={
                                                    'https://t.me/' +
                                                    settings.telegramBot
                                                }
                                                className="badge bg-secondary py-1 ms-1"
                                                tabIndex={-1}
                                            >
                                                ?
                                            </a>
                                        )}
                                        <input
                                            className="form-control"
                                            id="telegramChat"
                                            type="text"
                                            placeholder="Enter the Telegram Chat ID"
                                            value={settings.telegramChat || ''}
                                            onChange={onInputChange}
                                        />
                                    </div>
                                </div>
                                <div className="col-md-6 mb-3">
                                    <div className="form-group">
                                        <label htmlFor="plan">Plan</label>
                                        <input
                                            className="form-control"
                                            id="plan"
                                            type="text"
                                            disabled={true}
                                            value={
                                                settings.plan
                                                    ? settings.plan.name
                                                    : ''
                                            }
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className="row">
                                <div className="d-flex justify-content-between flex-wrap flex-md-nowrap">
                                    <div className="col-sm-3">
                                        <button
                                            className="btn btn-gray-800 mt-2 animate-up-2"
                                            type="button"
                                            onClick={onFormSubmit}
                                        >
                                            Save
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="row">
                    <div className="col-12">
                        <div className="card card-body border-0 shadow mb-4">
                            <h2 className="h5 mb-4">Your Plan</h2>
                            <div className="row align-items-end">
                                <div className="col-md-4 mb-3">
                                    <label htmlFor="planName">Plan</label>
                                    <input
                                        className="form-control"
                                        id="planName"
                                        type="text"
                                        disabled={true}
                                        value={settings.plan?.name || ''}
                                    />
                                </div>
                                {settings.plan?.isTrial &&
                                settings.trialExpiresAt ? (
                                    <div className="col-md-4 mb-3">
                                        <label htmlFor="trialExpiresAt">
                                            Trial expires at
                                        </label>
                                        <input
                                            className="form-control"
                                            id="trialExpiresAt"
                                            type="text"
                                            disabled={true}
                                            value={new Date(
                                                settings.trialExpiresAt,
                                            ).toLocaleString()}
                                        />
                                    </div>
                                ) : (
                                    <></>
                                )}
                                <div className="col-md-4 mb-3">
                                    <Link
                                        to={`/upgrade?email=${encodeURIComponent(settings.email || '')}`}
                                        state={{
                                            currentPlanId: settings.plan?.id,
                                        }}
                                        className="btn btn-gray-800 animate-up-2"
                                    >
                                        Request upgrade
                                    </Link>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="row">
                    <div className="col-12">
                        <div className="card card-body border-0 shadow mb-4">
                            <h2 className="h5 mb-2">
                                Email Notifications (SMTP){' '}
                                <span
                                    className={
                                        'badge ms-2 ' +
                                        (settings.channels &&
                                        settings.channels.email
                                            ? 'bg-success'
                                            : 'bg-secondary')
                                    }
                                >
                                    {settings.channels &&
                                    settings.channels.email
                                        ? 'Configured'
                                        : 'Not configured'}
                                </span>
                            </h2>
                            <p className="text-gray-600 small mb-4">
                                Email alerts from your automations are sent from
                                your own mailbox. Gmail example: server
                                smtp.gmail.com, port 587 (TLS), user is your
                                full Gmail address and the password is a Google
                                app password (Google Account, Security, 2-Step
                                Verification, App passwords), not your regular
                                password.
                            </p>
                            <div className="row">
                                <div className="col-md-6 mb-3">
                                    <div className="form-group">
                                        <label htmlFor="smtpHost">
                                            SMTP Server
                                        </label>
                                        <input
                                            className="form-control"
                                            id="smtpHost"
                                            type="text"
                                            placeholder={GMAIL_SMTP.smtpHost}
                                            value={settings.smtpHost || ''}
                                            onChange={onInputChange}
                                        />
                                    </div>
                                </div>
                                <div className="col-md-6 mb-3">
                                    <div className="form-group">
                                        <label htmlFor="smtpPort">
                                            Port (TLS)
                                        </label>
                                        <input
                                            className="form-control"
                                            id="smtpPort"
                                            type="number"
                                            placeholder={GMAIL_SMTP.smtpPort}
                                            value={settings.smtpPort || ''}
                                            onChange={onInputChange}
                                        />
                                    </div>
                                </div>
                            </div>
                            <div className="row">
                                <div className="col-md-6 mb-3">
                                    <div className="form-group">
                                        <label htmlFor="smtpUser">
                                            SMTP User
                                        </label>
                                        <input
                                            className="form-control"
                                            id="smtpUser"
                                            type="text"
                                            placeholder="yourname@gmail.com"
                                            value={settings.smtpUser || ''}
                                            onChange={onInputChange}
                                        />
                                    </div>
                                </div>
                                <div className="col-md-6 mb-3">
                                    <div className="form-group">
                                        <label htmlFor="smtpPass">
                                            SMTP Password
                                        </label>
                                        <input
                                            className="form-control"
                                            id="smtpPass"
                                            type="password"
                                            autoComplete="new-password"
                                            placeholder={
                                                settings.hasSmtpPass
                                                    ? 'Saved. Type to replace it'
                                                    : 'Your Google app password'
                                            }
                                            value={settings.smtpPass || ''}
                                            onChange={onInputChange}
                                        />
                                    </div>
                                </div>
                            </div>
                            <div className="row">
                                <div className="d-flex justify-content-between flex-wrap flex-md-nowrap">
                                    <div className="col-sm-9">
                                        <button
                                            className="btn btn-gray-800 mt-2 me-2 animate-up-2"
                                            type="button"
                                            onClick={onFormSubmit}
                                        >
                                            Save
                                        </button>
                                        <button
                                            className="btn btn-outline-gray-600 mt-2 me-2 animate-up-2"
                                            type="button"
                                            disabled={testing}
                                            onClick={onTestSmtpClick}
                                        >
                                            {testing
                                                ? 'Sending...'
                                                : 'Send test email'}
                                        </button>
                                        <button
                                            className="btn btn-link mt-2"
                                            type="button"
                                            onClick={onUseGmailClick}
                                        >
                                            Use Gmail values
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="row">
                    <div className="col-12">
                        <div className="card card-body border-0 shadow mb-4">
                            <h2 className="h5 my-4">Binance Settings</h2>
                            <div className="alert alert-info py-2 mb-3 small">
                                <div className="fw-bold mb-1">
                                    Recommended key permissions
                                </div>
                                <p className="text-gray-600 small mb-0">
                                    Create a dedicated key for the bot with{' '}
                                    <strong>Read</strong> and{' '}
                                    <strong>Spot &amp; Margin Trading</strong>{' '}
                                    permissions only. Never enable{' '}
                                    <strong>Withdrawals</strong> on a bot key,
                                    and restrict the key to your server IP if
                                    you can
                                    {settings.whitelistIps &&
                                    settings.whitelistIps.length > 0
                                        ? ' (see the addresses below)'
                                        : ''}
                                    .
                                </p>
                            </div>
                            {settings.whitelistIps &&
                                settings.whitelistIps.length > 0 && (
                                    <div className="alert alert-info py-2 mb-3 small">
                                        <div className="fw-bold mb-1">
                                            Binance API IP whitelist
                                        </div>
                                        <p className="text-gray-600 small mb-2">
                                            Restrict your API key to our server
                                            IP(s) below so it can only be used
                                            by Argos TraderBot: open Binance →
                                            API Management → Restricted access
                                            to trusted IPs, and add each
                                            address.
                                        </p>
                                        {settings.whitelistIps.map(ip => (
                                            <span
                                                key={ip}
                                                className="d-flex align-items-center mb-1"
                                            >
                                                <code>{ip}</code>
                                                <CopyButton text={ip} />
                                            </span>
                                        ))}
                                        {settings.keyIpRestricted === false && (
                                            <p className="text-danger small mb-0 mt-2">
                                                Your key is currently not
                                                IP-restricted.
                                            </p>
                                        )}
                                    </div>
                                )}
                            <div className="row">
                                <div className="col-12 mb-3">
                                    <div className="form-group">
                                        <label htmlFor="accessKey">
                                            Spot Access Key
                                        </label>
                                        <input
                                            className="form-control"
                                            id="accessKey"
                                            type="text"
                                            placeholder="Enter the Spot API Access Key"
                                            value={settings.accessKey || ''}
                                            onChange={onInputChange}
                                        />
                                    </div>
                                </div>
                            </div>
                            <div className="row">
                                <div className="col-12 mb-3">
                                    <div className="form-group">
                                        <label htmlFor="secretKey">
                                            New Spot Secret Key
                                        </label>
                                        <input
                                            className="form-control"
                                            id="secretKey"
                                            type="password"
                                            placeholder="Enter your new Spot API Secret Key"
                                            value={settings.secretKey || ''}
                                            onChange={onInputChange}
                                        />
                                    </div>
                                </div>
                            </div>
                            <div className="row">
                                <div className="col-12 mb-3">
                                    <button
                                        id="testSpotKeyBtn"
                                        className="btn btn-outline-gray-600 animate-up-2"
                                        type="button"
                                        disabled={testingSpotKey}
                                        onClick={() => onTestKeyClick(false)}
                                    >
                                        {testingSpotKey
                                            ? 'Testing...'
                                            : 'Test key'}
                                    </button>
                                    <KeyTestResult result={spotKeyResult} />
                                </div>
                            </div>
                            {settings &&
                            settings.plan &&
                            settings.plan.hasFutures ? (
                                <>
                                    <div className="row">
                                        <div className="col-12 mb-3">
                                            <div className="form-group">
                                                <label htmlFor="futuresKey">
                                                    Futures Access Key
                                                </label>
                                                <input
                                                    className="form-control"
                                                    id="futuresKey"
                                                    type="text"
                                                    placeholder="Enter the Futures API Access Key"
                                                    value={
                                                        settings.futuresKey ||
                                                        ''
                                                    }
                                                    onChange={onInputChange}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                    <div className="row">
                                        <div className="col-12 mb-3">
                                            <div className="form-group">
                                                <label htmlFor="futuresSecret">
                                                    New Futures Secret Key
                                                </label>
                                                <input
                                                    className="form-control"
                                                    id="futuresSecret"
                                                    type="password"
                                                    placeholder="Enter your new Futures API Secret Key"
                                                    value={
                                                        settings.futuresSecret ||
                                                        ''
                                                    }
                                                    onChange={onInputChange}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                    <div className="row">
                                        <div className="col-12 mb-3">
                                            <button
                                                id="testFuturesKeyBtn"
                                                className="btn btn-outline-gray-600 animate-up-2"
                                                type="button"
                                                disabled={testingFuturesKey}
                                                onClick={() =>
                                                    onTestKeyClick(true)
                                                }
                                            >
                                                {testingFuturesKey
                                                    ? 'Testing...'
                                                    : 'Test key'}
                                            </button>
                                            <KeyTestResult
                                                result={futuresKeyResult}
                                            />
                                        </div>
                                    </div>
                                </>
                            ) : (
                                <></>
                            )}
                            <div className="row">
                                <div className="d-flex justify-content-between flex-wrap flex-md-nowrap">
                                    <div className="col-sm-3">
                                        <button
                                            className="btn btn-gray-800 mt-2 animate-up-2"
                                            type="button"
                                            onClick={onFormSubmit}
                                        >
                                            Save
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <Footer />
            </main>
            <Toast
                text={notification.text}
                type={notification.type}
                onShow={() => setNotification({})}
            />
        </>
    );
}

export default Settings;
